/**
 * The preset form: create or edit a preset (name, node name, kind, categories, description,
 * the fields of the environment worked in (a language, code), auto-links) and where it is kept (library and folder). It edits a local
 * copy; nothing is saved before "Save preset".
 */
import { For, createMemo, createSignal, type JSX } from "solid-js";
import type { LinkKind } from "@protocol/LinkKind";
import type { NodeKind } from "@protocol/NodeKind";
import { useApp } from "../../app/context";
import type { PresetDraft } from "../../shared/drafts";
import type { PresetDraftPlace, SourceId } from "../../state/presets";
import { CategoryPicker } from "../../shared/CategoryPicker";
import { joinFolder } from "../../shared/presetTree";
import { toast } from "../../shared/toast";
import { FieldInputs } from "../../shared/FieldInputs";

/** The form to create or edit a preset. */
export function PresetForm(props: { draft: PresetDraftPlace }): JSX.Element {
  const { presets, graph, fields, kinds } = useApp();
  // A local copy, edited freely; saved only on "Save preset".
  const [draft, setDraft] = createSignal<PresetDraft>(structuredClone({ ...props.draft.preset, links: props.draft.preset.links.map((l) => ({ ...l })) }));
  // Where it is saved. Moving an existing preset to another library saves a copy there.
  const [source, setSource] = createSignal<SourceId>(props.draft.source);
  /** The folders of the chosen library, the draft's own included (made when saved). */
  const folders = createMemo(() => {
    const all = new Set(presets.source(source())?.library.folders ?? []);
    if (draft().folder) all.add(draft().folder);
    return ["", ...[...all].sort((a, b) => a.localeCompare(b))];
  });
  /** Changes some fields of the local copy. */
  const update = (patch: Partial<PresetDraft>) => setDraft((d) => ({ ...d, ...patch }));
  /** Node names offered as auto-link targets (the graph's, plus the draft's own). */
  const names = createMemo(() => {
    const all = new Set(graph.state.graph.nodes.map((n) => n.name));
    for (const l of draft().links) if (l.target) all.add(l.target);
    return [...all].sort((a, b) => a.localeCompare(b));
  });
  /** `true` when a node with this name or id exists. */
  const inGraph = (name: string) => graph.state.graph.nodes.some((n) => n.name === name || n.id === name);

  /** Saves the preset and closes the form. */
  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    const d = draft();
    if (!d.label.trim()) return toast("Give the preset a name.");
    // Saved to another library than the one it came from: a new preset there.
    const preset = source() === props.draft.source ? d : { ...d, id: "" };
    if (await presets.edit(source(), { edit: "save", preset })) {
      presets.setDraft(null);
      // Show it where it was saved.
      presets.go(source(), joinFolder("", d.folder));
      toast("Preset saved. Click it or drag it onto the graph to use it.");
    }
  };

  return (
    <>
      <div class="dhead">
        <strong>{props.draft.preset.id ? "Edit preset" : "New preset"}</strong>
      </div>
      <form class="pform dbody" id="preset-form" onSubmit={(e) => void submit(e)}>
        <div class="pcol">
          <label class="fl">
            Preset name
            <input class="field" id="preset-label" maxlength="40" value={draft().label} placeholder="For example GUI window" onInput={(e) => update({ label: e.currentTarget.value })} />
          </label>
          <label class="fl">
            Node name
            <input class="field" id="preset-name" maxlength="40" value={draft().name} placeholder="For example NewWindow" onInput={(e) => update({ name: e.currentTarget.value })} />
          </label>
          <div class="lrow">
            <label class="fl">
              Save to
              <select class="select" id="preset-source" onChange={(e) => setSource(e.currentTarget.value as SourceId)}>
                <For each={presets.writable()}>
                  {(s) => (
                    <option value={s.id} selected={s.id === source()}>
                      {s.label}
                    </option>
                  )}
                </For>
              </select>
            </label>
            <label class="fl">
              Folder
              <select class="select" id="preset-folder" onChange={(e) => update({ folder: e.currentTarget.value })}>
                <For each={folders()}>
                  {(f) => (
                    <option value={f} selected={f === draft().folder}>
                      {f || "(top)"}
                    </option>
                  )}
                </For>
              </select>
            </label>
          </div>
          <label class="fl">
            Kind
            <select class="select" onChange={(e) => update({ kind: e.currentTarget.value as NodeKind })}>
              <For each={kinds.nodes()}>
                {(k) => (
                  <option value={k.id} selected={k.id === draft().kind}>
                    {k.label}
                  </option>
                )}
              </For>
            </select>
          </label>
          <div class="fl">
            Categories
            <CategoryPicker id="preset-categories" value={draft().categories} known={graph.categories()} onChange={(categories) => update({ categories })} />
          </div>
        </div>
        <div class="pcol">
          <label class="fl">
            Description
            <input class="field" maxlength="140" value={draft().note} onInput={(e) => update({ note: e.currentTarget.value })} />
          </label>
          <FieldInputs idPrefix="preset" fields={fields.list()} data={draft().data} choices={fields.choices} onAdd={fields.add} onChange={(data) => update({ data })} />
        </div>
        <div class="pcol">
          <div class="fl">
            Auto-links
            <span class="hint2">Created with the node, towards nodes that exist in the graph (by name or id).</span>
            <For each={draft().links}>
              {(link, i) => (
                <div class="lrow">
                  <select
                    class="select"
                    aria-label="Link type"
                    onChange={(e) => update({ links: draft().links.map((l, j) => (j === i() ? { ...l, kind: e.currentTarget.value as LinkKind } : l)) })}
                  >
                    <For each={kinds.links()}>
                      {(k) => (
                        <option value={k.id} selected={k.id === link.kind}>
                          {k.label}
                        </option>
                      )}
                    </For>
                  </select>
                  <select class="select" aria-label="Link target" onChange={(e) => update({ links: draft().links.map((l, j) => (j === i() ? { ...l, target: e.currentTarget.value } : l)) })}>
                    <For each={names()}>
                      {(name) => (
                        <option value={name} selected={name === link.target}>
                          {name}
                          {inGraph(name) ? "" : " (not in graph)"}
                        </option>
                      )}
                    </For>
                  </select>
                  <button type="button" class="x" aria-label="Remove auto-link" onClick={() => update({ links: draft().links.filter((_, j) => j !== i()) })}>
                    ×
                  </button>
                </div>
              )}
            </For>
            <button
              type="button"
              class="btn"
              onClick={() => {
                const first = names()[0];
                if (!first) return toast("Add a node first, then it can be an auto-link target.");
                update({ links: [...draft().links, { kind: "uses", target: first }] });
              }}
            >
              Add auto-link
            </button>
          </div>
          <div class="btnrow">
            <button class="btn primary" type="submit">
              Save preset
            </button>
            <button class="btn" type="button" onClick={() => presets.setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
